import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import investmentService from '../../../services/investmentService';

/**
 * Investments Section
 * Displays investment accounts, holdings, and portfolio summary
 */
const InvestmentsSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [investments, setInvestments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      setLoading(true);
      setError(null);

      const [investmentsData, summaryData] = await Promise.all([
        investmentService.getInvestments(),
        investmentService.getInvestmentSummary()
      ]);

      setInvestments(investmentsData.investments || []);
      setSummary(summaryData.summary || null);
    } catch (err) {
      console.error('Error fetching investments:', err);
      setError(err.message || 'Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPrices = async () => {
    try {
      setSyncing(true);
      const result = await investmentService.syncAllPrices();
      
      toast.success(
        `Price sync complete! ${result.summary.totalUpdated} holdings updated.`
      );
      
      // Refresh data
      await fetchInvestments();
    } catch (err) {
      console.error('Error syncing prices:', err);
      toast.error('Failed to sync prices. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await investmentService.downloadTemplate();
      toast.success('CSV template downloaded successfully!');
    } catch (err) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  // Empty State
  if (!loading && investments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <AssessmentIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
        <Typography variant="h5" gutterBottom fontWeight="600">
          Start Tracking Your Investments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          Connect your brokerage and retirement accounts to see your portfolio, 
          holdings, and performance. We'll automatically update prices daily!
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/investments/manual-entry')}
          >
            Manual Entry
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<DownloadIcon />}
            onClick={() => navigate('/investments/csv-import')}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>💡 Pro Tip:</strong> Even with manual entry, we automatically 
            update your stock prices daily using real-time market data from Yahoo Finance!
          </Typography>
        </Box>
      </Box>
    );
  }

  // Loading State
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchInvestments}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  // Main Content with Investments
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Investment Portfolio
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Sync Prices">
            <IconButton
              onClick={handleSyncPrices}
              disabled={syncing}
              color="primary"
            >
              {syncing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/investments/manual-entry')}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Value
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(summary.totalValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Gain/Loss
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color={summary.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(summary.totalGainLoss)}
                </Typography>
                <Typography
                  variant="body2"
                  color={summary.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatPercent(summary.totalGainLossPercent)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Accounts
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.totalAccounts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Holdings
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {summary.totalHoldings}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Investment Accounts List */}
      <Grid container spacing={2}>
        {investments.map((investment) => (
          <Grid item xs={12} md={6} key={investment._id}>
            <Card sx={{ '&:hover': { boxShadow: 4 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="600">
                      {investment.accountName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {investment.brokerage} • {investment.accountType}
                    </Typography>
                  </Box>
                  <Chip
                    label={investment.entryType}
                    size="small"
                    color={investment.entryType === 'plaid' ? 'success' : 'default'}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Value
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(investment.totalValue)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={investment.totalGainLoss >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(investment.totalGainLoss)} ({formatPercent(investment.totalGainLossPercent)})
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {investment.holdings.length} {investment.holdings.length === 1 ? 'Holding' : 'Holdings'}
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/investments/${investment._id}`)}
                  >
                    View Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Need to add more investments?
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
          <Button size="small" onClick={() => navigate('/investments/manual-entry')}>
            Manual Entry
          </Button>
          <Button size="small" onClick={() => navigate('/investments/csv-import')}>
            Import CSV
          </Button>
          <Button size="small" onClick={handleDownloadTemplate}>
            Download Template
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InvestmentsSection;
